import React, { useMemo, useCallback, useRef, useEffect, useState } from 'react';
import { useFlexLayout } from 'react-table';
import { List } from 'react-window';
import {
  useHvTable,
  HvTableContainer,
  HvEmptyState,
  HvOverflowTooltip,
  useHvResizeColumns,
} from '@hitachivantara/uikit-react-core';
import { useTheme } from '@iq/react-components';

import Loader from '../../Loader';
import { DEFAULT_COLUMN } from '../AlarmPanel/utils';

// Default table layout constants
const DEFAULT_ROW_HEIGHT = 31;
const DEFAULT_CONTAINER_HEIGHT = 600;
const DEFAULT_HEADER_HEIGHT = 37;

// eslint-disable-next-line no-unused-vars -- Strip table metadata before spreading props onto DOM nodes.
const filterDomProps = ({ key, resizable, resizing, resizerProps, sortable, ...domProps }) =>
  domProps;

// Observes an element's rendered height and returns it, or null before the
// first measurement (or while disabled). Used to size the table to whatever
// height its container actually has, instead of assuming a fixed height.
const useMeasuredElementHeight = (elementRef, isEnabled) => {
  const [measuredHeight, setMeasuredHeight] = useState(null);

  useEffect(() => {
    if (!isEnabled) {
      setMeasuredHeight(null);
      return;
    }

    const element = elementRef.current;
    if (!element) {
      return;
    }

    const updateMeasuredHeight = (nextHeight) => {
      if (nextHeight > 0) {
        setMeasuredHeight(nextHeight);
      }
    };

    updateMeasuredHeight(Math.round(element.getBoundingClientRect().height));

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        updateMeasuredHeight(Math.round(entry.contentRect.height));
      }
    });

    resizeObserver.observe(element);

    return () => resizeObserver.disconnect();
  }, [elementRef, isEnabled]);

  return measuredHeight;
};

const HeaderLabel = ({ children, tooltip, className }) => {
  const theme = useTheme();
  const tooltipStyle =
    theme === 'dark'
      ? {
          backgroundColor: '#f2f2f2',
          color: '#000000',
        }
      : {
          backgroundColor: '#333333',
          color: '#ffffff',
        };

  return (
    <HvOverflowTooltip
      className={`${className}-header-label-tooltip`}
      data={children}
      title={tooltip || children}
      placement="top-start"
      slotProps={{
        tooltip: {
          className: `${className}-header-tooltip-popup`,
          style: tooltipStyle,
        },
      }}
    />
  );
};

const VirtualizedTable = ({
  columns,
  data,
  isLoading = false,
  emptyMessage = 'No data to display.',
  sortBy,
  sortOrder,
  onSortChange,
  onRowClick,
  onRowsRendered,
  rowHeight = DEFAULT_ROW_HEIGHT,
  containerHeight,
  headerHeight = DEFAULT_HEADER_HEIGHT,
  className = 'vt',
  loadMoreIndicator = false,
  fillAvailableHeight = false,
}) => {
  const wasResizingRef = useRef(false);
  const tableInnerRef = useRef(null);
  const hasExplicitContainerHeight = containerHeight !== undefined && containerHeight !== null;

  const shouldMeasureContainerHeight = fillAvailableHeight && !hasExplicitContainerHeight;
  const measuredContainerHeight = useMeasuredElementHeight(
    tableInnerRef,
    shouldMeasureContainerHeight
  );
  const resolvedContainerHeight = shouldMeasureContainerHeight
    ? measuredContainerHeight
    : (containerHeight ?? DEFAULT_CONTAINER_HEIGHT);
  const virtualizedListHeight = Math.max((resolvedContainerHeight ?? 0) - headerHeight, 0);
  const tableContainerStyle = hasExplicitContainerHeight ? { height: containerHeight } : undefined;

  const { getTableHeadProps, headerGroups, rows, prepareRow, state, totalColumnsWidth } =
    useHvTable(
      {
        columns,
        data,
        defaultColumn: DEFAULT_COLUMN,
      },
      useFlexLayout,
      useHvResizeColumns
    );

  const isResizing = !!state.columnResizing?.isResizingColumn;
  const columnResizing = state.columnResizing;
  const resizeKey = useMemo(
    () => JSON.stringify(columnResizing?.columnWidths || {}),
    [columnResizing?.columnWidths]
  );

  useEffect(() => {
    if (!isResizing && wasResizingRef.current) {
      const timer = setTimeout(() => {
        wasResizingRef.current = false;
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isResizing]);

  // Row component for react-window
  const RowComponent = useCallback(
    ({ index, style, rows: tableRows, prepareRow: prepare, onRowClick: handleRowClick }) => {
      const row = tableRows?.[index];
      if (!row) return null;
      prepare(row);

      const rowOriginal = row.original;
      const isClickable = !!handleRowClick;
      const clickableRowProps = isClickable
        ? {
            role: 'button',
            tabIndex: 0,
            onClick: () => handleRowClick(rowOriginal),
            onKeyDown: (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                handleRowClick(rowOriginal);
              }
            },
          }
        : { role: 'row' };

      return (
        <div
          style={{ ...style, cursor: isClickable ? 'pointer' : 'default' }}
          className={`${className}-row`}
          {...clickableRowProps}
        >
          {row.cells.map((cell) => {
            const cellProps = filterDomProps(cell.getCellProps());
            return (
              <div
                key={cell.column.id}
                className={`${className}-cell`}
                {...cellProps}
              >
                {cell.render('Cell')}
              </div>
            );
          })}
        </div>
      );
    },
    [className]
  );

  const rowData = useMemo(
    () => ({
      rows,
      prepareRow,
      onRowClick,
      columnResizing,
    }),
    [rows, prepareRow, onRowClick, columnResizing]
  );

  const headers = (
    <div
      {...filterDomProps(getTableHeadProps?.() || {})}
      className={`${className}-header`}
    >
      {headerGroups.map((headerGroup, index) => {
        const headerGroupProps = filterDomProps(headerGroup.getHeaderGroupProps());
        return (
          <div
            {...headerGroupProps}
            key={`${headerGroup.id}-${index}`}
          >
            {headerGroup.headers.map((col) => {
              const resizerProps = col.getResizerProps?.() || {};
              const headerProps = filterDomProps(col.getHeaderProps());
              const isSortable = col.sortable !== false;
              const isSorted = sortBy === col.id;

              const originalOnClick = headerProps.onClick;
              const wrappedHeaderProps = {
                ...headerProps,
                onClick: (event) => {
                  if (wasResizingRef.current || isResizing) {
                    event.stopPropagation();
                    return;
                  }
                  if (isSortable && onSortChange) {
                    onSortChange(col.id);
                  }
                  originalOnClick?.(event);
                },
                style: {
                  ...headerProps.style,
                  cursor: isSortable ? 'pointer' : 'default',
                },
              };

              return (
                <div
                  {...wrappedHeaderProps}
                  key={col.id}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      overflow: 'hidden',
                      minWidth: 0,
                    }}
                  >
                    <HeaderLabel
                      tooltip={col.headerTooltip}
                      className={className}
                    >
                      {col.render('Header')}
                    </HeaderLabel>
                    {isSortable && (
                      <span
                        style={{ fontSize: '12px', opacity: isSorted ? 1 : 0.3, flexShrink: 0 }}
                      >
                        {isSorted && sortOrder === 'ASC' ? '\u25B2' : '\u25BC'}
                      </span>
                    )}
                  </div>
                  {col.getResizerProps && (
                    <div
                      {...resizerProps}
                      onMouseDown={(event) => {
                        wasResizingRef.current = true;
                        resizerProps.onMouseDown?.(event);
                      }}
                      onTouchStart={(event) => {
                        wasResizingRef.current = true;
                        resizerProps.onTouchStart?.(event);
                      }}
                      className={`resizer ${col.isResizing ? 'isResizing' : ''}`}
                    />
                  )}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );

  const fillHeightClassName = fillAvailableHeight ? ' fill-height' : '';

  return (
    <div className={`${className}-list${fillHeightClassName}`}>
      <div className={`${className}-table-section${fillHeightClassName}`}>
        <HvTableContainer
          className={`${className}-table-container${fillHeightClassName}`}
          style={tableContainerStyle}
        >
          <div
            ref={tableInnerRef}
            className={`${className}-table-inner`}
          >
            {isLoading && data.length === 0 ? (
              <div className={`${className}-loader-container`}>
                <Loader />
              </div>
            ) : (
              <div style={{ minWidth: totalColumnsWidth }}>
                {rows.length > 0 ? (
                  <>
                    {headers}
                    <List
                      key={resizeKey}
                      style={{ width: '100%', height: virtualizedListHeight }}
                      rowCount={rows.length}
                      rowHeight={rowHeight}
                      rowComponent={RowComponent}
                      rowProps={rowData}
                      onRowsRendered={onRowsRendered}
                      className={`${className}-body custom-scrollbar`}
                    />
                  </>
                ) : (
                  <div className={`${className}-empty-state`}>
                    <HvEmptyState message={emptyMessage} />
                  </div>
                )}

                {loadMoreIndicator && rows.length === 0 && <Loader />}
              </div>
            )}
          </div>
        </HvTableContainer>
        {loadMoreIndicator && rows.length > 0 && (
          <div className={`${className}-load-more-overlay`}>
            <Loader />
          </div>
        )}
      </div>
    </div>
  );
};

export default VirtualizedTable;
